import React, { useEffect, useRef } from 'react';
import Icon from './Icon';

// A divider renders as a rule and carries no label; every other entry needs one.
export type ContextMenuItem =
  | { divider: true; label?: never }
  | {
    label: string;
    icon?: string;
    hint?: string;
    danger?: boolean;
    disabled?: boolean;
    divider?: false;
    onClick?: () => void;
  };

interface ContextMenuProps {
  items: ContextMenuItem[];
  x: number;
  y: number;
  onClose: () => void;
}

export default function ContextMenu({ items, x, y, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  // Viewport-aware positioning
  const menuW = 200;
  const menuH = items.length * 32 + 8;
  const left = x + menuW > window.innerWidth ? x - menuW : x;
  const top = y + menuH > window.innerHeight ? y - menuH : y;

  return (
    <div
      ref={ref}
      className="t-ctx-menu"
      style={{ left, top }}
      onContextMenu={e => e.preventDefault()}
    >
      {items.map((item, i) => {
        if (item.divider) return <div key={i} className="t-ctx-divider" />;
        return (
          <button
            key={i}
            className={['t-ctx-item', item.danger && 't-ctx-item--danger', item.disabled && 't-ctx-item--disabled'].filter(Boolean).join(' ')}
            onClick={() => { if (!item.disabled) { item.onClick?.(); onClose(); } }}
          >
            {item.icon && <Icon name={item.icon} size={13} />}
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.hint && <span className="t-ctx-hint">{item.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}
